import React, { useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { Flex, Show, useColorMode } from 'shared';
import { HotjarSuppression } from 'shared_DEPRECATED/components/Hotjar';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import {
  ReactionActionType,
  ReactionsType,
} from 'shared_DEPRECATED/components/Reactions/types';
import Spacer from 'shared_DEPRECATED/components/Spacer/Spacer';
import { Attachment } from 'shared_DEPRECATED/types/Attachment';

import {
  ChatMessageUnuploadedAttachments,
  ChatMessageUploadedAttachments,
} from 'features/chat/components/ChatMessages';
import { ChatMessageBubble } from 'features/chat/components/ChatMessages/ChatMessage/Bubble';
import { ChatMessageLinkPreview } from 'features/chat/components/ChatMessages/ChatMessage/LinkPreview';
import { ChatMessageReactions } from 'features/chat/components/ChatMessages/ChatMessage/Reactions';
import { ChatMessageSelector } from 'features/chat/components/ChatMessages/ChatMessage/Selector';
import { ChatMessageActions } from 'features/chat/components/ChatMessages/ChatMessageActions';
import { ChatMessageParent } from 'features/chat/components/ChatMessages/ChatMessageParent';
import { UnuploadedAttachment } from 'features/chat/config/types/Attachment';
import {
  MESSAGE_STATUS,
  PlainMessageContent,
  UUID,
} from 'features/chat/config/types/Message';
import { ParentMessageNullable } from 'features/chat/config/types/ParentMessage';
import { SendMorpheusMessage } from 'features/chat/config/types/types';
import { ChatMessageProvider } from 'features/chat/context/chatMessage';
import { useIsMorpheus3WayChatFFEnabled } from 'features/chat/hooks/useIsMorpheus3WayChatFFEnabled';
import { isChatMessagePlainType } from 'features/chat/utils/chatMessageUtils';

import { ButtonIcon } from 'shared/components/Button';
import { Typography } from 'shared/components/Typography';

import { ChatMessageText } from './Text';

type MessageWrapperProps = {
  id: UUID;
  text: string;
  createdAt: string;
  editedAt?: string;
  status: MESSAGE_STATUS;
  incoming: boolean;
  isMorpheus: boolean;
  markAsRead: (inView: boolean) => void;
  sendReaction: (
    messageId: UUID,
    emoji: string,
    type: ReactionActionType
  ) => void;
  retrySendingMessage: (id: string) => void;
  reactions: ReactionsType;
  attachments?: Attachment[] | UnuploadedAttachment[];
  isTemporary: boolean;
  replyToMessage: ParentMessageNullable;
  fromUserId: string;
  content: PlainMessageContent;
  sendMorpheusMessage: SendMorpheusMessage;
  shouldMorpheusButtonGlow: boolean;
  renderContent?: () => React.ReactNode;
};

export function MessageWrapper({
  id,
  text,
  createdAt,
  editedAt,
  status,
  incoming,
  isMorpheus,
  markAsRead,
  reactions,
  sendReaction,
  retrySendingMessage,
  attachments,
  isTemporary,
  replyToMessage,
  fromUserId,
  content,
  sendMorpheusMessage,
  shouldMorpheusButtonGlow,
  renderContent,
}: MessageWrapperProps) {
  const isInView = useRef(false);
  const { colorMode } = useColorMode();
  const isThemeLight = colorMode === 'light';
  const isMorpheus3WayChatFFEnabled = useIsMorpheus3WayChatFFEnabled();

  const handleMorpheusClick = () => {
    sendMorpheusMessage(text, id);
  };

  const { ref } = useInView({
    triggerOnce: true,
    skip: status === MESSAGE_STATUS.READ || !incoming,
    onChange: (inView) => {
      if (isInView.current) {
        return;
      }

      markAsRead(inView);
      isInView.current = inView;
    },
  });

  const iconName = getIconNameByStatus(status);
  const handleRetrySendingMessage = () => retrySendingMessage(id);

  return (
    <ChatMessageProvider isIncoming={incoming}>
      <HotjarSuppression>
        <ChatMessageSelector id={id} incoming={incoming}>
          <ChatMessageBubble incoming={incoming} isMorpheus={isMorpheus}>
            <Show when={replyToMessage}>
              {(replyToMessage) => (
                <ChatMessageParent
                  incoming={incoming}
                  replyToMessage={replyToMessage.text}
                  fromUserId={replyToMessage.fromUserId}
                  attachments={replyToMessage.attachments}
                  messageId={replyToMessage.messageId}
                />
              )}
            </Show>
            <Show
              when={attachments?.length}
              fallback={
                <ChatMessageLinkPreview
                  linkPreview={
                    isChatMessagePlainType(content) ? content.linkPreview : null
                  }
                  text={text}
                />
              }
            >
              <Spacer size="sm zr zr">
                <Spacer size="md">
                  {isTemporary ? (
                    <ChatMessageUnuploadedAttachments
                      attachments={attachments as UnuploadedAttachment[]}
                    />
                  ) : (
                    <ChatMessageUploadedAttachments
                      attachments={attachments as Attachment[]}
                    />
                  )}
                </Spacer>
              </Spacer>
            </Show>
            <Spacer size="md">
              <Show
                when={renderContent}
                fallback={
                  <ChatMessageText
                    text={text}
                    isLinkPreviewPresent={
                      isChatMessagePlainType(content) &&
                      !!content.linkPreview?.url &&
                      !attachments?.length
                    }
                  />
                }
              >
                {renderContent?.()}
              </Show>
              <Spacer size="sm zr zr" />
              <ChatMessageReactions
                theme={incoming || isMorpheus ? 'light' : 'dark'}
                id={id}
                reactions={reactions}
                sendReaction={sendReaction}
              />
              <Spacer size="sm zr zr" />
              <Flex
                justifyContent="flex-end"
                gap="var(--spacing01)"
                alignItems="center"
              >
                <Typography
                  type="small"
                  color={
                    isThemeLight
                      ? incoming || isMorpheus
                        ? 'black'
                        : 'white'
                      : incoming || isMorpheus
                      ? 'white'
                      : 'black'
                  }
                  dataTestid="chat-message-time"
                >
                  {editedAt ? `edited ${editedAt}` : createdAt}
                </Typography>
                {!incoming && (
                  <div>
                    {status === MESSAGE_STATUS.FAILED ? (
                      <ButtonIcon
                        aria-label="retry sending message"
                        onClick={handleRetrySendingMessage}
                      >
                        <Icon name={iconNames.exclamationMark} />
                      </ButtonIcon>
                    ) : (
                      <Icon name={iconName} fill="white" />
                    )}
                  </div>
                )}
              </Flex>
              <div ref={ref} />
            </Spacer>
          </ChatMessageBubble>
          <Show when={isMorpheus3WayChatFFEnabled}>
            <ButtonIcon
              animation={
                shouldMorpheusButtonGlow
                  ? 'morpheus-glow 2s ease-in-out infinite'
                  : undefined
              }
              onClick={handleMorpheusClick}
            >
              <Icon
                name={iconNames.sparkles}
                height="20px"
                width="20px"
                fill="var(--fgSecondary)"
              />
            </ButtonIcon>
          </Show>
          <ChatMessageActions
            isIncoming={incoming}
            messageId={id}
            text={text}
            attachments={attachments as Attachment[]}
            fromUserId={fromUserId}
            replyTo={replyToMessage?.messageId}
          />
        </ChatMessageSelector>
      </HotjarSuppression>
    </ChatMessageProvider>
  );
}

const getIconNameByStatus = (status: MESSAGE_STATUS) => {
  switch (status) {
    case MESSAGE_STATUS.SENT:
      return iconNames.tick;
    case MESSAGE_STATUS.READ:
      return iconNames.doubleTick;
    case MESSAGE_STATUS.CREATED:
    default:
      return iconNames.time;
  }
};
